import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import type ISheetProps from "./type";
import {
    getSheetForm,
    getSheetEdge
} from "./stylesheet";
import SheetBody from "./components/body";
import SheetClose from "./components/close";
import SheetContent from "./components/content";
import SheetFooter from "./components/footer";
import SheetHeader from "./components/header";
import SheetTrigger from "./components/trigger";
import {
    SheetContext
} from "./context";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import {
    useBreakpoint
} from "../../helpers/pointer";
import {
    warnOnce
} from "../../utils/development";
import Dialog from "../dialog";

const Sheet: FC<ISheetProps> = ({
    isCloseOnOverlay = true,
    isDefaultOpen = false,
    isOpen: isOpenProp,
    confirmCloseProps,
    compactBehaviour,
    isDirty = false,
    size = "medium",
    customLocalize,
    isModal = true,
    side = "right",
    onOpenChange,
    customTheme,
    isVisible,
    children,
    onClosed,
    onClose,
    onOpen,
    width
}) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const breakpoint = useBreakpoint();

    const baseId = useId();

    const [
        isOpenState,
        setIsOpenState
    ] = useState(isDefaultOpen);

    const [
        isConfirmVisible,
        setIsConfirmVisible
    ] = useState(false);

    const [
        isScrolled,
        setIsScrolled
    ] = useState(false);

    const controlledOpen = isOpenProp ?? isVisible;

    const isControlled = controlledOpen !== undefined;

    const isOpen = isControlled ? controlledOpen : isOpenState;

    const isOpenRef = useRef(isOpen);

    isOpenRef.current = isOpen;

    const edge = getSheetEdge({
        side
    });

    const form = getSheetForm({
        compactBehaviour,
        breakpoint,
        isModal,
        edge
    });

    useEffect(() => {
        if(isVisible !== undefined) {
            warnOnce({
                message: "Sheet: \"isVisible\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
            });
        }

        if(onOpen) {
            warnOnce({
                message: "Sheet: \"onOpen\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
            });
        }

        if(onClose) {
            warnOnce({
                message: "Sheet: \"onClose\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
            });
        }
    }, [
        isVisible,
        onClose,
        onOpen
    ]);

    useEffect(() => {
        if(isOpen) {
            return;
        }

        setIsConfirmVisible(false);

        setIsScrolled(false);
    }, [
        isOpen
    ]);

    const handleRequestOpen = useCallback(() => {
        if(onOpenChange) onOpenChange(true);

        if(onOpen) onOpen();

        if(!isControlled) {
            setIsOpenState(true);
        }
    }, [
        isControlled,
        onOpenChange,
        onOpen
    ]);

    const handleClose = useCallback(() => {
        if(!isOpenRef.current) {
            return;
        }

        if(!isControlled) {
            setIsOpenState(false);
        }

        if(onOpenChange) onOpenChange(false);

        if(onClose) onClose();
    }, [
        isControlled,
        onOpenChange,
        onClose
    ]);

    const handleRequestClose = useCallback(() => {
        if(isDirty) {
            setIsConfirmVisible(true);

            return;
        }

        handleClose();
    }, [
        handleClose,
        isDirty
    ]);

    const handleClosed = useCallback(() => {
        if(onClosed) onClosed();
    }, [
        onClosed
    ]);

    const handleConfirmClosed = () => {
        setIsConfirmVisible(false);

        if(confirmCloseProps?.onClosed) confirmCloseProps.onClosed({
            id: baseId
        });
    };

    const renderConfirm = () => {
        return <Dialog
            content={localize("sheet-unsaved-desc")}
            title={localize("sheet-unsaved-title")}
            contentJustify="language-based"
            customLocalize={customLocalize}
            customTheme={customTheme}
            variant="yes-no"
            type="danger"
            {...confirmCloseProps}
            secondaryButtonProps={{
                title: localize("sheet-keep-editing"),
                ...confirmCloseProps?.secondaryButtonProps,
                onPress: ({
                    closeAnimation
                }) => {
                    closeAnimation();

                    if(confirmCloseProps?.secondaryButtonProps?.onPress) confirmCloseProps.secondaryButtonProps.onPress({
                        closeAnimation
                    });
                }
            }}
            primaryButtonProps={{
                title: localize("sheet-discard-changes"),
                ...confirmCloseProps?.primaryButtonProps,
                onPress: ({
                    closeAnimation
                }) => {
                    closeAnimation();

                    handleClose();

                    if(confirmCloseProps?.primaryButtonProps?.onPress) confirmCloseProps.primaryButtonProps.onPress({
                        closeAnimation
                    });
                }
            }}
            isOpen={isConfirmVisible && isOpen}
            onClosed={handleConfirmClosed}
            onOverlayPress={({
                closeAnimation
            }) => {
                closeAnimation();
            }}
            isShowClose={false}
        />;
    };

    const context = useMemo(() => ({
        descriptionId: `${baseId}-description`,
        requestClose: handleRequestClose,
        contentId: `${baseId}-content`,
        requestOpen: handleRequestOpen,
        titleId: `${baseId}-title`,
        onClosed: handleClosed,
        isCloseOnOverlay,
        customLocalize,
        setIsScrolled,
        customTheme,
        isScrolled,
        isModal,
        isDirty,
        isOpen,
        width,
        form,
        edge,
        size
    }), [
        handleRequestClose,
        handleRequestOpen,
        isCloseOnOverlay,
        customLocalize,
        handleClosed,
        customTheme,
        isScrolled,
        isModal,
        isDirty,
        baseId,
        isOpen,
        width,
        form,
        edge,
        size
    ]);

    return <SheetContext.Provider
        value={context}
    >
        {children}
        {renderConfirm()}
    </SheetContext.Provider>;
};
export default Object.assign(Sheet, {
    Content: SheetContent,
    Trigger: SheetTrigger,
    Footer: SheetFooter,
    Header: SheetHeader,
    Close: SheetClose,
    Body: SheetBody
});
